
import {
  BookingV3,
  CustomerBookingV3,
  ErrorBookingV3,
  CommodityBookingV3,
  BookingFormType,
  AddressType,
  ConfigValidationForm
} from "@/domain/entities/CreateBookingV3";
import { Options, prop, Vue } from "vue-class-component";
import Checkbox from "primevue/checkbox";
import { BookingController } from "@/app/ui/controllers/BookingController";
import debounce from "lodash/debounce";
import {
  receiverSchema,
  receiverPhoneSchema,
  receiverSenderSchema,
  receiverDestinationCitySchema,
  receiverPostalCodeSchema
} from "./validation";
import {
  parseMultiFieldZodError,
  parseZodError,
  validationCurrentIndex
} from "../../utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { getPhoneDisplayCode } from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { RequestListCommodity } from "@/data/payload/api/CommodityApiRequest";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { removeNewLineWhitespace } from "@/app/infrastructures/misc/Utils";
import { CountryData, DistrictData, ZipCodeEntities } from "@/domain/entities/Location";
import { AccountEntities } from "@/domain/entities/Account";
import { OptionsClass } from "@/domain/entities/MainApp";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { CustomerListEntities } from "@/domain/entities/Booking";
import { shouldSkipPhoneValidation } from "../../../booking-utils";

type CustomerParams = {
  search: string;
  isReceiver?: boolean;
};

class Props {
  modelValue = prop<BookingV3>({
    default: new BookingV3(),
    type: Object
  });
  errors = prop<ErrorBookingV3>({
    default: new ErrorBookingV3(),
    type: Object
  });
  formType = prop<string>({
    default: BookingFormType.MANUAL,
    type: String
  });
  currentIndex = prop<number>({
    default: -1,
    type: Number
  });
  receiverNameIndex = prop<number>({
    default: 4,
    type: Number
  });
  receiverPhoneIndex = prop<number>({
    default: 5,
    type: Number
  });
  receiverAddressIndex = prop<number>({
    default: 6,
    type: Number
  });
  receiverDestinationCityIndex = prop<number>({
    default: 7,
    type: Number
  });
  receiverPostalCodeIndex = prop<number>({
    default: 8,
    type: Number
  });
  receiverAddressTypeIndex = prop<number>({
    default: 9,
    type: Number
  });
  saveReceiverIndex = prop<number>({
    default: 10,
    type: Number
  });
  configName = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configPhone = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAddress = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configDistrict = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configPostalCode = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configSaveAddress = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });

  isClientDO = prop<boolean>({
    default: false,
    type: Boolean
  });

  isDO = prop<boolean>({
    default: false,
    type: Boolean
  });
  listKGs = prop<any>({
    default: [],
    type: Array
  });
  isEditForm = prop<boolean>({
    default: false
  });
  isErrorEmbargo = prop<boolean>({
    default: false
  });
  isReverseJourneyDetail = prop<boolean>({
    default: false
  });
  isRebookingFlow = prop<boolean>({
    default: false
  });
  isErrorRouteNotFound = prop<boolean>({
    default: false
  });
  isBookingShipment = prop<boolean>({
    default: false
  });
  isPhoneCodeSet = prop<boolean>({
    default: false
  });
  countryCodeButtonId = prop<string>({
    default: "button_country_code_receiver_booking",
    type: String
  });
  phoneNumberFieldId = prop<string>({
    default: "field_phone_number_receiver_booking",
    type: String
  });
}

@Options({
  components: {
    Checkbox
  },
  emits: [
    "update:currentIndex",
    "currentIndevalue",
    "update-commodity",
    "change-district",
    "search-postal-code",
    "update:isErrorRouteNotFound"
  ],
  watch: {
    "modelValue.addressReceiver": {
      handler() {
        if (!this.isRebookingFlow) {
          this.onValidateAddress();
        }
      }
    }
  }
})
export default class DetailReceiver extends Vue.with(Props) {
  async mounted() {
    if (!this.isPhoneCodeSet || !this.modelValue.phoneCodeReceiver) {
      this.modelValue.phoneCodeReceiver = getPhoneDisplayCode(
        !this.accountIsForeign ? "ID" : "MY"
      );
      this.modelValue.phoneFlagReceiver = !this.accountIsForeign
        ? "indonesia"
        : "malaysia";
    }

    if (this.isBookingManual) {
      this.getCustomerList();
    }
    if (this.isEditForm) {
      this.fetchDestinationCity("");
    }

    await this.getCountryList();
    this.onUpdateDestinationCountryDetail();
  }

  get disabledName() {
    return this.configName.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledPhone() {
    return this.configPhone.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledAddress() {
    return this.configAddress.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledDistrict() {
    return this.configDistrict.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledPostalCode() {
    return this.configPostalCode.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }
  get disabledSaveAddress() {
     if (this.isReverseJourneyDetail) return true;
     return this.configSaveAddress.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get accountData(): AccountEntities {
    return AccountController.accountData;
  }
  get accountIsForeign(): boolean {
    return this.accountData.countryCode !== "id";
  }
  get isBookingClient() {
    return this.formType === BookingFormType.CLIENT;
  }
  get isBookingManual() {
    return this.formType === BookingFormType.MANUAL;
  }

  // receiver name
  get loadingCustomerList(): boolean {
    return BookingController.isLoadingCustomer;
  }
  get customerList() {
    if ( this.isBookingClient && !this.modelValue.clientName.id || this.isReverseJourneyDetail ) return [];
    return BookingController.senderCustomerList.data || [];
  }
  fetchCustomer = debounce((search: string) => {
    this.getCustomerList({
      search,
      isReceiver: true
    });
  }, 1000);
  get customerRef() {
    return {
      refType:
        !AccountController.accountData.isPosAccount &&
        this.isBookingClient &&
        this.modelValue.clientName.id
          ? "client"
          : "",
      refId: AccountController.accountData.isPosAccount
        ? 0
        : this.modelValue.clientName.id ?? 0
    };
  }
  getCustomerList(params?: CustomerParams) {
    if (this.isBookingClient && !this.modelValue.clientName.id || this.isReverseJourneyDetail) return;
    BookingController.getCustomerList({
      ...params,
      cache: true,
      search: params?.search || "",
      refType: this.customerRef.refType,
      refId: this.customerRef.refId
    });
  }
  onChangeName(value: string) {
    const trimmed = value.trim();
    this.modelValue.receiver = trimmed;
      this.onValidateName();
     if (!trimmed || !this.errorMessageName) {
      this.fetchCustomer(trimmed);
    }
  }
  onFocusCustomer(index: number, search: string) {
    this.onSelectCurrentIndex(index);
    this.getCustomerList({
      search,
      isReceiver: true
    });
  }
  async onSelectCustomer(item: CustomerBookingV3, isCODorDFOF: boolean) {
    if (!item || this.isReverseJourneyDetail ) return false;
    if (
      item.customerDistrictId && 
      ((this.isEditForm && !isCODorDFOF) || (!this.isEditForm))
    ) {
      const detail = await this.fetchDistrictDetail(
        String(item.customerDistrictId)
      );
      this.modelValue.destinationCity = detail;
      this.onUpdateDestinationCountryDetail();
      this.fetchDestinationCity("");


    await this.$nextTick()
    const { originZipCode } = this.destinationCity;
    if (!detail.destinationListZipCode.includes(originZipCode)) {
      detail.destinationListZipCode = [originZipCode, ...detail.destinationListZipCode];
    }
      const postalCodeListOptions = this.buildPostalCodeOptions(detail);
      LocationController.zipCodeData.zipCodeData = postalCodeListOptions;
      this.setPostalCodeDestination(detail, postalCodeListOptions);
      await this.$nextTick();
      this.handleValidationAndEmit();
    }

    this.modelValue.receiver = item.customerName;
    this.modelValue.phoneCodeReceiver = item.customerPhoneCode;
    this.modelValue.phoneReceiver = item.customerPhoneNumber;
    this.modelValue.addressReceiver = item.customerAddress;
    this.modelValue.phoneFlagReceiver = item.customerPhoneflag;

    this.modelValue.addressReceiverType = new OptionsClass(
      this.listAddressType.find(
        (e) =>
          e.value ===
          (AddressType[item.customerAddressType as keyof typeof AddressType] ||
            AddressType.rumah)
      )
    );

    this.onValidateName();
    this.onValidatePhone();
    this.onValidateAddress();
  }
  get errorMessageName(): string {
    if (validationCurrentIndex(this.receiverNameIndex, this.currentIndex)) {
      return this.onValidateName();
    }

    return this.errors.receiver;
  }
  onValidateName() {
    const message = parseZodError(receiverSchema, this.modelValue.receiver);
    this.errors.receiver = message;

    return message;
  }

  // receiver phone number
  isTooltipLimited = false;
  toggleTooltipLimited(event: any) {
    this.isTooltipLimited = !this.isTooltipLimited;

    const refs: any = this.$refs;
    refs?.["limited"]?.toggle(event);
  }
  get errorMessagePhone(): string {
    if (validationCurrentIndex(this.receiverPhoneIndex, this.currentIndex)) {
      return this.onValidatePhone();
    }

    return this.errors.phoneReceiver;
  }
  onValidatePhone() {
    if (shouldSkipPhoneValidation(this.fullPhoneNumber, this.disabledPhone)) {
      this.errors.phoneReceiver = "";
      return "";
    }

    const message = parseZodError(receiverPhoneSchema, this.fullPhoneNumber);
    this.errors.phoneReceiver = message;

    return message;
  }
  get fullPhoneNumber() {
    const { phoneCodeReceiver, phoneReceiver } = this.modelValue;
    return phoneReceiver
      ? `${phoneCodeReceiver}${phoneReceiver}`.replace("+", "")
      : phoneReceiver;
  }
  onChangePhone() {
    const { phoneCodeReceiver, phoneReceiver } = this.modelValue;

    this.modelValue.phoneCodeReceiver = phoneCodeReceiver;
    this.modelValue.phoneReceiver = phoneReceiver;

    this.onValidatePhone();

    if (!this.errorMessagePhone) {
      this.fetchCustomer(this.fullPhoneNumber);
    }
  }

  // receiver address
  get errorMessageAddress(): string {
    if (
      validationCurrentIndex(this.receiverAddressIndex, this.currentIndex) ||
      this.modelValue.addressReceiver
    ) {
      return this.onValidateAddress();
    }

    return this.errors.addressReceiver;
  }
  onValidateAddress() {
    const message = parseMultiFieldZodError(
      receiverSenderSchema,
      {
        addressReceiver: this.modelValue.addressReceiver,
        addressSender: this.modelValue.addressSender
      },
      "addressReceiver"
    );
    this.errors.addressReceiver = message;

    return message;
  }
  get maxAddressLength() {
    return FlagsMedusa.config_max_address.getValue();
  }
  replaceNewLine(value: string) {
    const removedNewLineAddress = removeNewLineWhitespace(value, " ");

    this.modelValue.addressReceiver = removedNewLineAddress;

    return removedNewLineAddress;
  }

  // destination
  get destinationCity(): DistrictData {
    if (typeof this.modelValue.destinationCity === "string") {
      return new DistrictData();
    }
    return this.modelValue.destinationCity;
  }
  get errorMessageDestination(): string {
    if (
      validationCurrentIndex(
        this.receiverDestinationCityIndex,
        this.currentIndex
      )
    ) {
      return this.onValidateDestinationCity();
    }
    return this.errors.destinationCity;
  }
  onValidateDestinationCity() {
    const message = parseZodError(receiverDestinationCitySchema, {
      destination: this.destinationCity?.name,
      embargo: this.modelValue.destinationIsEmbargo,
      unavailable: this.modelValue.destinationIsUnavailable
    });
    this.errors.destinationCity = message;

    return message;
  }
  get isLoadingDestinationCity() {
    return LocationController.isLoadingDistrict;
  }
  dataListDistrict: DistrictData[] = [];
  currentPageDistrict = 1;
  get destinationCityList(): DistrictData[] {
    return this.dataListDistrict
      .filter((item: DistrictData) => item.status.toLowerCase() === "active")
      .map(
        (e: DistrictData) =>
          new DistrictData({
            ...e,
            name: `${e.name}, ${e.cityName}`,
            countryCode: "ID"
          })
      );
  }
  async fetchDistrictList(search: string, page = 1): Promise<DistrictData[]> {
    return await LocationController.getDistrictList({
      search,
      status: "active",
      page,
      limit: 20,
      excludeCountry: this.modelValue.productName === "INTERPACK" ? "ID" : "",
      type: this.districtType,
      cache: true
    });
  }
  searchDestinationCity(value: string) {
    this.fetchDestinationCity(value);
  }
  fetchDestinationCity = debounce(async (search: string) => {
    this.currentPageDistrict = 1;
    const data = await this.fetchDistrictList(search, 1);
    this.dataListDistrict = data;
  }, 250);
  async onScrollDistrict(search: string) {
    const nextPage = this.currentPageDistrict + 1;
    const data = await this.fetchDistrictList(search, nextPage);

    this.dataListDistrict = [...this.dataListDistrict, ...data];
    this.currentPageDistrict = nextPage;
  }
  get districtType() {
    return this.accountData.account_type === "client" &&
      (this.isDO || this.isClientDO)
      ? "within-city,forward-area"
      : "";
  }
  onFocusDestinationCity() {
    this.onSelectCurrentIndex(this.receiverDestinationCityIndex);

    if (this.errorMessageDestination) return;
    this.onGetDestinationCity();
  }

  onGetDestinationCity() {
    if (!this.destinationCityList.length || !this.modelValue.destinationCity) {
      LocationController.setLoadingDistrict(true);
      this.fetchDestinationCity("");
    }
  }
  setCountryCode(detail: DistrictData) {
    if (this.modelValue.destinationCity instanceof DistrictData)
      (this.modelValue.destinationCity as any).countryCode = detail.countryCode;
  }
  async onSelectDestinationCity() {
    const { id } = this.destinationCity;
    if (!id) return;

    const detail = await this.fetchDistrictDetail(String(id));
    this.setCountryCode(detail);

    this.onUpdateDestinationCountryDetail();

    await this.$nextTick()
    const { originZipCode } = this.destinationCity;
    if (!detail.destinationListZipCode.includes(originZipCode)) {
      detail.destinationListZipCode = [originZipCode, ...detail.destinationListZipCode];
    }
    const postalCodeListOptions = this.buildPostalCodeOptions(detail);
    LocationController.zipCodeData.zipCodeData = postalCodeListOptions;
      await this.$nextTick();
    this.modelValue.destinationCity = new DistrictData({
      ...detail,
      name: `${detail.name}, ${detail.cityName}`
    });

    this.setPostalCodeDestination(detail, postalCodeListOptions);
    this.handleValidationAndEmit();
  }
  clearSearch() {
    this.$emit("update:isErrorRouteNotFound", false);
  }
  handleValidationAndEmit() {
    this.onValidateDestinationCity();
    this.$emit("select-district");
  }
  async fetchDistrictDetail(id: string) {
    return await LocationController.getDetailDistrict({
      id,
      notSaveDetail: true
    });
  }
  buildPostalCodeOptions(detail: any): DistrictData[] {
    const { originZipCode } = this.destinationCity;
    const postalCodeData: string[] = detail.destinationListZipCode.length
      ? detail.destinationListZipCode
      : originZipCode
      ? [originZipCode]
      : [];

    return postalCodeData.map(
      (value) =>
        new DistrictData({
          ...detail,
          name: value,
          districtName: detail.name
        })
    );
  }
  setPostalCodeDestination(detail: any, options: DistrictData[]) {
    const { originZipCode } = this.destinationCity;
    const postalCode = detail.destinationZipCode || originZipCode;
    if (!postalCode || !options.length) return;

    const postalCodeFromDestination = new DistrictData({
      ...detail,
      name: originZipCode,
      districtName: detail.name
    });

    this.modelValue.postalCodeDestination = postalCode.includes(",")
      ? originZipCode
        ? postalCodeFromDestination
        : options[0]
      : postalCodeFromDestination;
    this.$emit(
      "update:postalCodeDestination",
      this.modelValue.postalCodeDestination
    );

    this.onValidatePostalCode();
  }
  get vendorTypeForDestinationDistrict() {
    return ["vendor", "vendor_lanjutan"].includes(
      this.destinationCity?.type?.toLowerCase() ?? ""
    );
  }
  get vendorCodeForDestinationDistrict() {
    return ["ninja", "nx", "pi"].includes(
      this.destinationCity?.vendorCode?.toLowerCase() ?? ""
    );
  }
  get checkVendorDestinationDistrict(): boolean {
    const res =
      this.vendorTypeForDestinationDistrict &&
      this.vendorCodeForDestinationDistrict &&
      this.listKGs.length >= 1;
    this.modelValue.isVendorNinjaDestination = res;
    return res;
  }
  // Purely derived from the selected district's flag; never written into
  // `errors.destinationCity` or passed to `onValidateDestinationCity()`, so
  // it stays non-blocking and cannot invalidate the field (FR-FE-08).
  get showMisbookingGuidance(): boolean {
    return this.destinationCity?.isMisbookingPotential === "yes";
  }

  // postal code
  get postalCode(): DistrictData {
    if (typeof this.modelValue.postalCodeDestination === "string") {
      return new DistrictData();
    }
    return this.modelValue.postalCodeDestination;
  }
  get errorMessagePostalCode(): string {
    if (
      validationCurrentIndex(this.receiverPostalCodeIndex, this.currentIndex)
    ) {
      return this.onValidatePostalCode();
    }

    return this.errors.postalCode;
  }
  onValidatePostalCode() {
    const message = parseZodError(
      receiverPostalCodeSchema,
      this.postalCode?.name || ""
    );
    this.errors.postalCode = message;

    return this.isBookingShipment ? "" : message;
  }
  get postalCodeList() {
    return LocationController.zipCodeData.zipCodeData;
  }
  allowOnlyNumeric(event: KeyboardEvent) {
    const isNumber = /^[0-9]$/;
    const input = event.target as HTMLInputElement;

    if (!isNumber.test(event.key)) {
      event.preventDefault();
    }

    if (input.value.length >= 12) {
      event.preventDefault();
    }
  }

  onGetPostalCodeList = debounce(async (search: string) => {
    if (search) {
      this.modelValue.postalCodeDestination = new DistrictData({
        id: Number(search),
        name: search
      });
      this.$emit(
        "update:postalCodeDestination",
        new DistrictData({
          id: Number(search),
          name: search
        })
      );
    }

    if (this.disabledDistrict) {
      this.onValidatePostalCode();
      return LocationController.setZipCodeData(
        new ZipCodeEntities(
          LocationController.zipCodeData.pagination,
          LocationController.zipCodeData.zipCodeData.filter((zipcode) => {
            return zipcode.name.startsWith(search);
          })
        )
      );
    }

    const response: ZipCodeEntities = await LocationController.getZipCodeList({
      search: search,
      page: 1,
      limit: 10
    });
    const data = response.zipCodeData.map(
      (item: DistrictData) =>
        new DistrictData({
          ...item,
          name: item.originZipCode + " - " + item.name + ", " + item.cityName,
          districtName: item.name
        })
    );
    LocationController.setZipCodeData(
      new ZipCodeEntities(response.pagination, data)
    );

    this.onValidatePostalCode();
  }, 250);

  get eligibleAutofillDistrict() {
    return (
      !this.destinationCity?.originZipCode &&
      this.modelValue.postalCodeDestination
    );
  }
  async onChangePostalCode(value: string | DistrictData) {
    if (!value && !this.disabledDistrict) {
      this.modelValue.destinationCity = "";
    }

    if (
      this.eligibleAutofillDistrict ||
      (value as DistrictData)?.name?.includes("-")
    ) {
      const detail = await this.fetchDistrictDetail(String(this.postalCode.id));
      this.setCountryCode(detail);


      const { districtName, cityName } = this.postalCode;

      this.modelValue.destinationCity = new DistrictData({
        ...this.postalCode,
        name: districtName + ", " + cityName,
        countryCode: detail.countryCode
      });
      await this.$nextTick();
      this.setCountryCode(detail);
      this.onUpdateDestinationCountryDetail();

      await this.$nextTick()
      const { originZipCode } = this.destinationCity;
      if (!detail.destinationListZipCode.includes(originZipCode)) {
        detail.destinationListZipCode = [originZipCode, ...detail.destinationListZipCode];
      }
      const postalCodeListOptions = this.buildPostalCodeOptions(detail);
      LocationController.zipCodeData.zipCodeData = postalCodeListOptions;
      this.setPostalCodeDestination(detail, postalCodeListOptions);
      this.handleValidationAndEmit();
      // NOTE: already set postalcode value:
      // this.modelValue.postalCodeDestination = new DistrictData({
      //   ...this.postalCode,
      //   name: this.postalCode.originZipCode
      // });
    }

    this.onValidatePostalCode();
  }

  // address type
  get listAddressType(): OptionsClass[] {
    return [
      new OptionsClass({
        name: this.$t("bookingShipment.column.office"),
        value: AddressType.kantor
      }),
      new OptionsClass({
        name: this.$t("bookingShipment.column.home"),
        value: AddressType.rumah
      })
    ];
  }
  onSelectAddressType(item: OptionsClass) {
    this.modelValue.addressReceiverType = item;
    this.onSelectCurrentIndex(this.receiverAddressTypeIndex);
  }

  // save address
  onSelectCurrentIndex(index: number) {
    setTimeout(() => {
      if (!validationCurrentIndex(index, this.currentIndex)) {
        this.$emit("update:currentIndex", index);
      }
    }, 0);
  }
  async onScroll(search: string) {
    const nextPage = BookingController.senderCustomerList.pagination.page + 1;
    const data = await BookingController.getCustomerInfiniteScrollList({
      cache: true,
      search: search || "",
      refType: this.customerRef.refType,
      refId: this.customerRef.refId,
      isSender: false,
      page: nextPage
    });
    

    BookingController.setSenderCustomerList(new CustomerListEntities(
      data.pagination,
      [...this.customerList, ...data.data]
    ));
  }

  get isEditShipment() {
    return this.isEditForm && !!this.modelValue?.sttShipmentId;
  }

  countryList = [] as CountryData[];
  async getCountryList() {
    const res = await LocationController.getCountryList({
      search: "",
      status: "active",
      page: 1,
      limit: 200
    });

    if (!res?.countryData) return;

    this.countryList = res.countryData;
  }

  onUpdateDestinationCountryDetail() {
    const selectedCountryCode = (this.modelValue
      .destinationCity as DistrictData)?.countryCode;

    if (!selectedCountryCode) return;

    this.modelValue.destinationCountry = new CountryData(
      this.countryList.find((item) => item.code === selectedCountryCode)
    );
  }
}
